import { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import Modal from './Modal.jsx';
import CategoryCombobox from './CategoryCombobox.jsx';
import ShopifyOrderAutocomplete from './ShopifyOrderAutocomplete.jsx';
import { useCategories } from '../hooks/useCategories.js';
import { useCreatePettyCashMovement, useUpdatePettyCashMovement } from '../hooks/usePettyCash.js';

const TODAY = new Date().toISOString().slice(0, 10);

function getDefaultForm(mode, movement) {
  if (mode === 'edit' && movement) {
    return {
      kind: movement.kind,
      amount: movement.amount_cents > 0 ? String(movement.amount_cents / 100) : '',
      movement_date: movement.movement_date ?? TODAY,
      category_id: movement.category_id ?? null,
      comment: movement.comment ?? '',
      shopify_order_id: movement.shopify_order_id ?? null,
    };
  }
  return {
    kind: 'out',
    amount: '',
    movement_date: TODAY,
    category_id: null,
    comment: '',
    shopify_order_id: null,
  };
}

/**
 * Modal de saisie d'un mouvement de caisse.
 *
 * Props :
 *   open          — booléen
 *   onClose       — callback
 *   mode          — 'create' | 'edit' | 'opening'
 *   movement      — objet mouvement (mode edit uniquement)
 */
export default function PettyCashFormModal({ open, onClose, mode = 'create', movement }) {
  const isOpening = mode === 'opening';
  const isEdit = mode === 'edit';

  const [form, setForm] = useState(() => {
    if (isOpening) {
      return {
        kind: 'opening',
        amount: '',
        movement_date: TODAY,
        category_id: null,
        comment: '',
        shopify_order_id: null,
      };
    }
    return getDefaultForm(mode, movement);
  });
  const [errors, setErrors] = useState({});
  const [selectedOrder, setSelectedOrder] = useState(null);

  const createMut = useCreatePettyCashMovement();
  const updateMut = useUpdatePettyCashMovement();

  // Récupération des catégories pour filtrage par flow_type
  const { data: categoriesData } = useCategories();
  const rawCategories = Array.isArray(categoriesData)
    ? categoriesData
    : categoriesData?.categories ?? categoriesData?.data ?? [];

  // Filtrer les catégories selon le sens du mouvement
  const filteredCategories = rawCategories.filter((c) => {
    if (form.kind === 'in') return c.flow_type === 'income';
    if (form.kind === 'out') return c.flow_type === 'expense';
    return false; // opening : pas de catégorie
  });

  // Reset le formulaire quand la modal s'ouvre
  useEffect(() => {
    if (open) {
      if (isOpening) {
        setForm({
          kind: 'opening',
          amount: '',
          movement_date: TODAY,
          category_id: null,
          comment: '',
          shopify_order_id: null,
        });
      } else {
        setForm(getDefaultForm(mode, movement));
      }
      setErrors({});
      setSelectedOrder(null);
    }
  }, [open, mode, movement, isOpening]);

  // Reset la catégorie quand on change de sens (pour éviter un category_id incompatible)
  function handleKindChange(newKind) {
    setForm((f) => ({ ...f, kind: newKind, category_id: null }));
  }

  function validate() {
    const e = {};
    const amountVal = parseFloat(form.amount);
    if (!form.amount || isNaN(amountVal) || amountVal <= 0) {
      e.amount = 'Le montant doit être supérieur à 0';
    }
    if (!form.movement_date) {
      e.movement_date = 'La date est requise';
    }
    if (!isOpening && !form.category_id) {
      e.category_id = 'La catégorie est requise';
    }
    return e;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const payload = {
      kind: form.kind,
      amount_cents: Math.round(parseFloat(form.amount) * 100),
      movement_date: form.movement_date,
      ...(form.category_id ? { category_id: form.category_id } : {}),
      ...(form.comment.trim() ? { comment: form.comment.trim() } : {}),
      ...(form.shopify_order_id ? { shopify_order_id: form.shopify_order_id } : {}),
    };

    try {
      if (isEdit && movement) {
        const { kind: _kind, ...updatePayload } = payload;
        await updateMut.mutateAsync({ id: movement.id, ...updatePayload });
      } else {
        await createMut.mutateAsync(payload);
      }
      onClose();
    } catch (err) {
      setErrors({ submit: err.message ?? 'Une erreur est survenue' });
    }
  }

  const isPending = createMut.isPending || updateMut.isPending;

  const title = isOpening
    ? 'Solde d\'ouverture'
    : isEdit
    ? 'Modifier le mouvement'
    : 'Nouveau mouvement';

  return (
    <Modal open={open} onClose={onClose} title={title} size="md">
      <form onSubmit={handleSubmit} noValidate className="space-y-4">

        {/* Sens (in/out) — désactivé en mode edit et opening */}
        {!isOpening && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Sens
            </label>
            <div className="flex gap-2" role="group" aria-label="Sens du mouvement">
              <button
                type="button"
                onClick={() => handleKindChange('in')}
                disabled={isEdit}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg border text-sm font-medium transition-colors min-h-[40px] ${
                  form.kind === 'in'
                    ? 'bg-green-50 border-green-400 text-green-700'
                    : 'border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-default'
                }`}
                aria-pressed={form.kind === 'in'}
              >
                <TrendingUp size={15} aria-hidden />
                Entrée
              </button>
              <button
                type="button"
                onClick={() => handleKindChange('out')}
                disabled={isEdit}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg border text-sm font-medium transition-colors min-h-[40px] ${
                  form.kind === 'out'
                    ? 'bg-red-50 border-red-400 text-red-700'
                    : 'border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-default'
                }`}
                aria-pressed={form.kind === 'out'}
              >
                <TrendingDown size={15} aria-hidden />
                Sortie
              </button>
            </div>
          </div>
        )}

        {/* Montant */}
        <div>
          <label htmlFor="petty-amount" className="block text-sm font-medium text-slate-700 mb-1">
            Montant (€) <span aria-hidden className="text-red-500">*</span>
          </label>
          <input
            id="petty-amount"
            type="number"
            min="0.01"
            step="0.01"
            inputMode="decimal"
            value={form.amount}
            onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
            placeholder="0,00"
            className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 transition-colors ${
              errors.amount ? 'border-red-400 bg-red-50' : 'border-slate-300 bg-white'
            }`}
            aria-invalid={Boolean(errors.amount)}
            aria-describedby={errors.amount ? 'petty-amount-error' : undefined}
          />
          {errors.amount && (
            <p id="petty-amount-error" className="mt-1 text-xs text-red-600">{errors.amount}</p>
          )}
        </div>

        {/* Date */}
        <div>
          <label htmlFor="petty-date" className="block text-sm font-medium text-slate-700 mb-1">
            Date <span aria-hidden className="text-red-500">*</span>
          </label>
          <input
            id="petty-date"
            type="date"
            value={form.movement_date}
            onChange={(e) => setForm((f) => ({ ...f, movement_date: e.target.value }))}
            className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 transition-colors ${
              errors.movement_date ? 'border-red-400 bg-red-50' : 'border-slate-300 bg-white'
            }`}
            aria-invalid={Boolean(errors.movement_date)}
          />
          {errors.movement_date && (
            <p className="mt-1 text-xs text-red-600">{errors.movement_date}</p>
          )}
        </div>

        {/* Catégorie — masquée pour opening */}
        {!isOpening && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Catégorie <span aria-hidden className="text-red-500">*</span>
            </label>
            <CategoryCombobox
              value={form.category_id}
              onChange={(id) => setForm((f) => ({ ...f, category_id: id }))}
              placeholder={
                form.kind === 'in'
                  ? 'Catégorie de recette…'
                  : 'Catégorie de dépense…'
              }
              categories={filteredCategories}
            />
            {errors.category_id && (
              <p className="mt-1 text-xs text-red-600">{errors.category_id}</p>
            )}
          </div>
        )}

        {/* Commentaire */}
        <div>
          <label htmlFor="petty-comment" className="block text-sm font-medium text-slate-700 mb-1">
            Commentaire
          </label>
          <textarea
            id="petty-comment"
            value={form.comment}
            onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))}
            placeholder="Description, note…"
            rows={2}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400 resize-none transition-colors"
          />
        </div>

        {/* Commande Shopify — masquée pour opening */}
        {!isOpening && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Commande Shopify
              <span className="ml-1 text-xs text-slate-400 font-normal">(optionnel)</span>
            </label>
            <ShopifyOrderAutocomplete
              value={form.shopify_order_id}
              onChange={(id) => {
                setForm((f) => ({ ...f, shopify_order_id: id }));
                if (!id) setSelectedOrder(null);
              }}
              selectedOrder={selectedOrder}
            />
          </div>
        )}

        {/* Erreur globale */}
        {errors.submit && (
          <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
            {errors.submit}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors min-h-[40px]"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={isPending}
            className="flex-1 rounded-lg bg-slate-800 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors min-h-[40px]"
          >
            {isPending ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
